import React, { useRef, useState, useEffect, useCallback } from 'react';
import { ReelItem, ReelProduct } from '../types';
import { ProductTag } from './ProductTag';
import { ProductDrawer } from './ProductDrawer';
import './ReelsStream.css';

interface ReelsStreamProps {
  reels: ReelItem[];
  currentIndex: number;
  onNext: () => void;
  onPrev: () => void;
  sessionId: string | null;
  trackEvent: (eventName: string, properties?: Record<string, unknown>) => void;
}

export const ReelsStream: React.FC<ReelsStreamProps> = ({
  reels,
  currentIndex,
  onNext,
  onPrev,
  sessionId,
  trackEvent
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRefs = useRef<Map<number, HTMLVideoElement>>(new Map());
  const touchStartY = useRef<number>(0);
  const touchStartTime = useRef<number>(0);
  const viewStartTime = useRef<number>(Date.now());
  
  const [isMuted, setIsMuted] = useState(true);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [showScrollHint, setShowScrollHint] = useState(true);
  const [isTagShaking, setIsTagShaking] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isTagExpanding, setIsTagExpanding] = useState(false);
  const [isDrawerMorphing, setIsDrawerMorphing] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<ReelProduct | null>(null);

  const currentReel = reels[currentIndex];

  useEffect(() => {
    // Hide scroll hint after user interaction or delay
    if (currentIndex > 0) {
      setShowScrollHint(false);
    } else {
      const timer = setTimeout(() => setShowScrollHint(false), 4000);
      return () => clearTimeout(timer);
    }
  }, [currentIndex]);

  const trackReelView = useCallback((reelId: string, watchDuration: number) => {
    trackEvent('reels_view', { reelId, watchDuration });
  }, [trackEvent]);

  useEffect(() => {
    viewStartTime.current = Date.now();

    return () => {
      if (currentReel) {
        const duration = Math.floor((Date.now() - viewStartTime.current) / 1000);
        if (duration > 0) {
          trackReelView(currentReel.id, duration);
        }
      }
    };
  }, [currentIndex, currentReel, trackReelView]);

  useEffect(() => {
    videoRefs.current.forEach((video, index) => {
      if (index === currentIndex) {
        video.currentTime = 0;
        const playPromise = video.play();
        if (playPromise !== undefined) {
          playPromise.catch((error) => {
            console.log('Video autoplay prevented:', error);
          });
        }
      } else {
        video.pause();
      }
    });
  }, [currentIndex]);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartY.current = e.touches[0].clientY;
    touchStartTime.current = Date.now();
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    e.preventDefault();
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    const touchEndY = e.changedTouches[0].clientY;
    const deltaY = touchStartY.current - touchEndY;
    const deltaTime = Date.now() - touchStartTime.current;
    
    const minSwipeDistance = 50;
    const maxSwipeTime = 300;

    if (Math.abs(deltaY) > minSwipeDistance && deltaTime < maxSwipeTime) {
      setIsTransitioning(true);
      
      // Shake the tag on swipe
      setIsTagShaking(true);
      setTimeout(() => setIsTagShaking(false), 500);
      
      if (deltaY > 0 && currentIndex < reels.length - 1) {
        onNext();
      } else if (deltaY < 0 && currentIndex > 0) {
        onPrev();
      }

      setTimeout(() => setIsTransitioning(false), 300);
    }
  };

  const handleProductTagClick = (product: ReelProduct) => {
    setSelectedProduct(product);
    setIsTagExpanding(true);
    setIsDrawerMorphing(true);
    
    // Delay drawer open for morphing effect
    setTimeout(() => {
      setIsDrawerOpen(true);
      setIsTagExpanding(false);
    }, 300);
    
    // Remove morphing class after animation
    setTimeout(() => {
      setIsDrawerMorphing(false);
    }, 800);
  };

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
    setIsDrawerMorphing(false);
  };

  const handleVideoClick = () => {
    setIsMuted(!isMuted);
    
    const currentVideo = videoRefs.current.get(currentIndex);
    if (currentVideo) {
      currentVideo.muted = !isMuted;
    }
  };

  const setVideoRef = (index: number, el: HTMLVideoElement | null) => {
    if (el) {
      videoRefs.current.set(index, el);
    } else {
      videoRefs.current.delete(index);
    }
  };

  if (reels.length === 0) {
    return (
      <div className="reels-stream reels-stream--empty">
        <p>Henüz video içeriği yok</p>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="reels-stream"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <div
        className="reels-stream__track"
        style={{
          transform: `translateY(-${currentIndex * 100}%)`,
          transition: isTransitioning ? 'transform 0.3s ease-out' : 'none'
        }}
      >
        {reels.map((reel, index) => (
          <div key={reel.id} className="reels-stream__item">
            <video
              ref={(el) => setVideoRef(index, el)}
              src={reel.videoUrl}
              className="reels-stream__video"
              loop
              muted={isMuted}
              playsInline
              onClick={handleVideoClick}
              poster={reel.thumbnailUrl}
            />

            <div className="reels-stream__overlay">
            </div>

            {reel.product && index === currentIndex && (
              <ProductTag
                product={reel.product}
                isShaking={isTagShaking}
                isExpanding={isTagExpanding}
                onClick={() => handleProductTagClick(reel.product!)}
              />
            )}
          </div>
        ))}
      </div>

      <div className="reels-stream__indicators">
        {reels.map((_, index) => (
          <div
            key={index}
            className={`reels-stream__indicator ${
              index === currentIndex ? 'active' : ''
            } ${index < currentIndex ? 'viewed' : ''}`}
          />
        ))}
      </div>

      <div className="reels-stream__navigation">
        <button
          className={`reels-stream__nav-btn reels-stream__nav-btn--up ${
            currentIndex === 0 ? 'disabled' : ''
          }`}
          onClick={onPrev}
          disabled={currentIndex === 0}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="18 15 12 9 6 15" />
          </svg>
        </button>
        <button
          className={`reels-stream__nav-btn reels-stream__nav-btn--down ${
            currentIndex === reels.length - 1 ? 'disabled' : ''
          }`}
          onClick={onNext}
          disabled={currentIndex === reels.length - 1}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>
      </div>

      <div className="reels-stream__counter">
        {currentIndex + 1} / {reels.length}
      </div>

      {showScrollHint && (
        <div className="reels-stream__scroll-hint">
          <div className="reels-stream__scroll-hint-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 5v14M19 12l-7 7-7-7" />
            </svg>
          </div>
          <span>Kaydır</span>
        </div>
      )}

      <ProductDrawer
        product={selectedProduct}
        isOpen={isDrawerOpen}
        isMorphing={isDrawerMorphing}
        onClose={handleDrawerClose}
      />
    </div>
  );
};

export default ReelsStream;
